import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@kollez/design-system';
import './styles/tailwind.css';
import '@kollez/design-system/styles.css';
import { router } from './router';
import { useAuthStore } from './lib/authStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Apply persisted theme before first paint.
(function applyStoredTheme() {
  const stored = window.localStorage.getItem('kollez-theme');
  const q = new URLSearchParams(window.location.search).get('theme');
  const theme = q === 'ruled' || q === 'blackboard' ? q : stored === 'ruled' ? 'ruled' : 'blackboard';
  document.documentElement.setAttribute('data-theme', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'blackboard' ? '#1F40AF' : '#FFF8EA');
})();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

function AppRoot() {
  const init = useAuthStore((s) => s.init);
  useEffect(() => { void init(); }, [init]);
  return <RouterProvider router={router} />;
}

// (Notification stream hook is opted-in on individual pages — AppShell mounts it once the user
// is authenticated. Adding it here would fire before auth hydration is done.)

// Guard against HMR calling createRoot twice on the same DOM element in dev.
type WindowWithRoot = Window & { __kollezRoot__?: ReactDOM.Root };
const w = window as unknown as WindowWithRoot;
if (!w.__kollezRoot__) {
  w.__kollezRoot__ = ReactDOM.createRoot(rootEl);
}
w.__kollezRoot__.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Toaster>
          <AppRoot />
        </Toaster>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
